import { Skeleton, Text } from '@mantine/core';
import type { FC, ReactNode } from 'react';

import { neutralColors } from '@styles/design-system';

import styles from './StatCard.module.css';

export interface StatCardProps {
    label: string;
    value: ReactNode;
    helper?: string;
    valueColor?: string;
    loading?: boolean;
    /**
     * Makes the card the way in to what it counts.
     *
     * A figure that reports a problem and cannot be acted on sends somebody hunting for the
     * list it came from. Optional, because plenty of figures have nowhere sensible to go.
     */
    onClick?: () => void;
}

const StatCard: FC<StatCardProps> = ({
    label,
    value,
    helper,
    valueColor,
    loading,
    onClick,
}) => (
    <div
        className={`${styles.card} ${onClick ? styles.clickable : ''}`}
        onClick={onClick}
        onKeyDown={(event) => {
            if (!onClick) return;
            if (event.key === 'Enter' || event.key === ' ') onClick();
        }}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
    >
        <Text
            style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: neutralColors.textMuted,
            }}
        >
            {label}
        </Text>

        {loading ? (
            <Skeleton height={32} width={80} mt={4} mb={4} />
        ) : (
            <Text
                style={{
                    fontSize: 24,
                    fontWeight: 600,
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                    color: valueColor ?? neutralColors.textPrimary,
                    marginTop: 4,
                    marginBottom: 4,
                }}
            >
                {value}
            </Text>
        )}

        {helper && (
            <Text style={{ fontSize: 12, color: neutralColors.textMuted, lineHeight: 1.4 }}>
                {helper}
            </Text>
        )}
    </div>
);

export default StatCard;
